import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import { auditApi, AuditLog, AuditLogPage } from '../services/api';

const ACTION_LABELS: Record<string, string> = {
  LOGIN: '登录',
  CREATE: '创建',
  UPDATE: '更新',
  DELETE: '删除',
  UPLOAD: '上传',
};

const ACTION_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  LOGIN: 'info',
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
  UPLOAD: 'primary',
};

const RESOURCE_LABELS: Record<string, string> = {
  USER: '用户',
  DOCUMENT: '文档',
};

function formatTime(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
}

const AuditLogManagement: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('ALL');
  const [successFilter, setSuccessFilter] = useState<string>('ALL');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data: AuditLogPage = await auditApi.list(page, rowsPerPage);
      setLogs(data.content || []);
      setTotalElements(data.totalElements ?? 0);
    } catch (e: any) {
      const message =
        e.response?.status === 403
          ? '无权限查看审计日志（仅超级管理员可访问）'
          : e.response?.data?.message || e.message || '加载审计日志失败';
      setError(message);
      setLogs([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (actionFilter !== 'ALL' && log.action !== actionFilter) return false;
      if (successFilter === 'SUCCESS' && !log.success) return false;
      if (successFilter === 'FAIL' && log.success) return false;
      return true;
    });
  }, [logs, actionFilter, successFilter]);

  const actionOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.action).filter(Boolean));
    return Array.from(set).sort();
  }, [logs]);

  return (
    <Box>
      <PageHeader
        title="审计日志"
        subtitle="记录登录、文档与用户等关键操作，便于合规追溯与安全排查"
        breadcrumbs={[{ label: '系统管理' }, { label: '审计日志' }]}
        action={
          <Chip
            icon={<RefreshIcon />}
            label={loading ? '加载中…' : '刷新'}
            onClick={loadLogs}
            clickable
            variant="outlined"
            disabled={loading}
          />
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>操作类型</InputLabel>
            <Select
              label="操作类型"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="ALL">全部</MenuItem>
              {actionOptions.map((action) => (
                <MenuItem key={action} value={action}>
                  {ACTION_LABELS[action] || action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>结果</InputLabel>
            <Select
              label="结果"
              value={successFilter}
              onChange={(e) => setSuccessFilter(e.target.value)}
            >
              <MenuItem value="ALL">全部</MenuItem>
              <MenuItem value="SUCCESS">成功</MenuItem>
              <MenuItem value="FAIL">失败</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              共 {totalElements} 条记录
            </Typography>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>时间</TableCell>
              <TableCell>用户</TableCell>
              <TableCell>操作</TableCell>
              <TableCell>资源</TableCell>
              <TableCell>资源 ID</TableCell>
              <TableCell>详情</TableCell>
              <TableCell>IP</TableCell>
              <TableCell align="center">结果</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '加载中…' : '暂无审计记录'}
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(log.createdAt)}</TableCell>
                  <TableCell>{log.username || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ACTION_LABELS[log.action] || log.action}
                      color={ACTION_COLORS[log.action] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{RESOURCE_LABELS[log.resourceType || ''] || log.resourceType || '—'}</TableCell>
                  <TableCell>{log.resourceId || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={log.detail || ''}>
                      {log.detail || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.ipAddress || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={log.success ? '成功' : '失败'}
                      color={log.success ? 'success' : 'error'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="每页"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default AuditLogManagement;
